import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from "@tanstack/react-query";

// Centralized roles fetching
async function fetchUserRoles(userId: string) {
  const [
    { data: isAdminResult },
    { data: isSuperAdminResult },
    { data: isUnassignedAdminResult },
    { data: isManagerResult },
    { data: adminTeams },
    { data: rolesData },
    { data: contributorData }
  ] = await Promise.all([
    supabase.rpc("is_admin", { user_id: userId }),
    supabase.rpc("is_super_admin", { user_id: userId }),
    supabase.rpc("is_unassigned_admin", { user_id: userId }),
    supabase.rpc("is_manager", { user_id: userId }),
    supabase.from("admin_teams").select("team_id").eq("admin_id", userId),
    supabase.from("user_roles").select("role").eq("user_id", userId),
    supabase.from("admin_contributors").select("id").eq("contributor_admin_id", userId).maybeSingle()
  ]);

  const roles = (rolesData || []).map((r: any) =>
    typeof r.role === "string" ? r.role : String(r.role)
  );

  return {
    roles,
    isAdmin: isAdminResult === true,
    isSuperAdmin: isSuperAdminResult === true,
    isUnassignedAdmin: isUnassignedAdminResult === true,
    isManager: isManagerResult === true,
    adminTeamIds: (adminTeams || []).map(t => t.team_id),
    isContributor: !!contributorData,
  };
}

interface UserContextType {
  user: User | null;
  roles: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isUnassignedAdmin: boolean;
  isManager: boolean;
  adminTeamIds: string[];
  isContributor: boolean;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  canAccessTeam: (teamId: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const profileUpdateCache = new Map<string, number>();
const PROFILE_UPDATE_COOLDOWN = 60000;

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const profileUpdateInProgressRef = useRef(false);

  // Fetch roles using React Query - only when user exists
  const rolesQuery = useQuery({
    queryKey: ["userRoles", user?.id],
    queryFn: () => fetchUserRoles(user!.id),
    enabled: !!user?.id,
    staleTime: 120_000, // 2 minutes
    gcTime: 600_000, // 10 minutes cache
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    // Set up auth listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      setAuthLoading(false);
      
      // Defer profile updates to prevent blocking auth state
      if (session?.user) {
        setTimeout(() => {
          updateUserProfile(session.user);
        }, 0);
      }
    });

    // THEN get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      setAuthLoading(false);
      
      if (session?.user) {
        setTimeout(() => {
          updateUserProfile(session.user);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const updateUserProfile = async (user: User) => {
    if (profileUpdateInProgressRef.current) return;
    
    const lastUpdate = profileUpdateCache.get(user.id);
    const now = Date.now();
    if (lastUpdate && (now - lastUpdate < PROFILE_UPDATE_COOLDOWN)) {
      return;
    }
    
    profileUpdateInProgressRef.current = true;
    
    try {
      const { data: existingProfile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
        
      if (error) {
        console.error("Error fetching profile:", error);
        profileUpdateCache.set(user.id, now);
        return;
      }
      
      if (!existingProfile) {
        await createNewProfile(user);
      } else if (!isProfileComplete(existingProfile)) {
        await updateIncompleteProfile(user, existingProfile);
      }
      
      profileUpdateCache.set(user.id, now);
    } catch (error) {
      console.error("Error managing profile:", error);
      profileUpdateCache.set(user.id, now);
    } finally {
      profileUpdateInProgressRef.current = false;
    }
  };
  
  const createNewProfile = async (user: User) => {
    let fullName = user.user_metadata?.full_name || 
                user.user_metadata?.name || 
                `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
                user.email?.split('@')[0];
                
    if (fullName && fullName === user.email?.split('@')[0]) {
      fullName = fullName.charAt(0).toUpperCase() + fullName.slice(1);
    }
    
    if (!fullName) {
      fullName = 'User';
    }
                
    await supabase.from('profiles').insert({
      id: user.id,
      email: user.email,
      full_name: fullName,
      first_name: user.user_metadata?.first_name || user.user_metadata?.given_name,
      last_name: user.user_metadata?.last_name || user.user_metadata?.family_name,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  };
  
  const updateIncompleteProfile = async (user: User, profile: any) => {
    let fullName = user.user_metadata?.full_name || 
                user.user_metadata?.name || 
                `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
                profile.email?.split('@')[0] || 
                user.email?.split('@')[0];
    
    if (fullName && (fullName === profile.email?.split('@')[0] || 
                    fullName === user.email?.split('@')[0])) {
      fullName = fullName.charAt(0).toUpperCase() + fullName.slice(1);
    }
    
    if (!fullName) {
      fullName = 'User';
    }
    
    const updates = {
      full_name: fullName,
      first_name: user.user_metadata?.first_name || user.user_metadata?.given_name || profile.first_name,
      last_name: user.user_metadata?.last_name || user.user_metadata?.family_name || profile.last_name,
      email: user.email || profile.email,
      updated_at: new Date().toISOString()
    };
    
    const finalUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    
    await supabase.from('profiles').update(finalUpdates).eq('id', user.id);
  };
  
  const isProfileComplete = (profile: any): boolean => {
    return (
      profile.full_name && 
      profile.full_name !== 'Unknown User' && 
      !profile.full_name.startsWith('User-')
    );
  };

  const defaults = { 
    roles: [] as string[], 
    isAdmin: false, 
    isSuperAdmin: false,
    isUnassignedAdmin: false,
    isManager: false,
    adminTeamIds: [] as string[],
    isContributor: false
  };

  const rolesData = (user ? rolesQuery.data : defaults) || defaults;

  const value: UserContextType = {
    user,
    roles: rolesData.roles,
    isAdmin: rolesData.isAdmin,
    isSuperAdmin: rolesData.isSuperAdmin,
    isUnassignedAdmin: rolesData.isUnassignedAdmin,
    isManager: rolesData.isManager,
    adminTeamIds: rolesData.adminTeamIds,
    isContributor: rolesData.isContributor,
    isLoading: authLoading || (user ? rolesQuery.isLoading : false),
    hasRole: (role: string) => rolesData.roles.some((r) => r.toLowerCase() === role.toLowerCase()),
    canAccessTeam: (teamId: string) => {
      if (rolesData.isSuperAdmin) return true;
      if (rolesData.isUnassignedAdmin) return false;
      return rolesData.adminTeamIds.includes(teamId);
    },
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Legacy hook compatibility - now just uses context
export const useSupabaseUser = () => {
  const { user } = useUser();
  return { user };
};

// Legacy hook compatibility - now just uses context
export const useUserRoles = () => {
  const context = useUser();
  return {
    roles: context.roles,
    isAdmin: context.isAdmin,
    isSuperAdmin: context.isSuperAdmin,
    isUnassignedAdmin: context.isUnassignedAdmin,
    isManager: context.isManager,
    adminTeamIds: context.adminTeamIds,
    isContributor: context.isContributor,
    isLoading: context.isLoading,
    error: null,
    hasRole: context.hasRole,
    canAccessTeam: context.canAccessTeam,
  };
};
